import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  FormControl
} from '@mui/material';
import { CheckCircle, XCircle, Clock, Users, Lock } from 'lucide-react';
import AdminPasswordReset from './AdminPasswordReset';

const AdminDashboard = ({ user, apiRequest }) => {
  const [userRequests, setUserRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null });
  const [roleUpdateLoading, setRoleUpdateLoading] = useState(null);
  const [teamLevelUpdateLoading, setTeamLevelUpdateLoading] = useState(null);
  const [passwordResetDialog, setPasswordResetDialog] = useState({ open: false, user: null });

  const fetchUserRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiRequest('/api/admin/users');

      if (res.ok) {
        const data = await res.json();
        setUserRequests(data.users || []);
        setError(null);
      } else {
        setError('Failed to fetch user requests');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  const handleUserAction = async (userId, action) => {
    try {
      setActionLoading(userId);
      const res = await apiRequest(`/api/admin/users/${userId}/${action}`, {
        method: 'PATCH'
      });

      if (res.ok) {
        await fetchUserRequests(); // Refresh the list
        setError(null);
      } else {
        setError(`Failed to ${action} user`);
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setActionLoading(null);
      setConfirmDialog({ open: false, action: null, user: null });
    }
  };

  const handleRoleUpdate = async (userId, newRole) => {
    try {
      setRoleUpdateLoading(userId);
      const res = await apiRequest(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (res.ok) {
        await fetchUserRequests(); // Refresh the list
        setError(null);
      } else {
        setError('Failed to update user role');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setRoleUpdateLoading(null);
    }
  };

  const handleTeamLevelUpdate = async (userId, newTeamLevel) => {
    try {
      setTeamLevelUpdateLoading(userId);
      const res = await apiRequest(`/api/admin/users/${userId}/team-level`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ team_level: newTeamLevel })
      });

      if (res.ok) {
        await fetchUserRequests(); // Refresh the list
        setError(null);
      } else {
        setError('Failed to update user team level');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setTeamLevelUpdateLoading(null);
    }
  };

  const openConfirmDialog = (action, userData) => {
    setConfirmDialog({
      open: true,
      action,
      user: userData
    });
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip icon={<Clock size={16} />} label="Pending" color="warning" size="small" />;
      case 'approved':
        return <Chip icon={<CheckCircle size={16} />} label="Approved" color="success" size="small" />;
      case 'rejected':
        return <Chip icon={<XCircle size={16} />} label="Rejected" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getRoleChip = (role) => {
    return (
      <Chip
        label={role === 'admin' ? 'Admin' : 'User'}
        color={role === 'admin' ? 'primary' : 'default'}
        size="small"
      />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchUserRequests();
  }, [fetchUserRequests]);

  if (!user?.isAdmin) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6, p: 3 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 6, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Users size={32} style={{ marginRight: '12px' }} />
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={3}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            User Registration Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve new user registrations
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Team Level</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Registration Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                  <TableCell align="center">Password</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No user requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  userRequests.map((userRequest) => (
                    <TableRow key={userRequest.id}>
                      <TableCell>
                        <Typography variant="body1">
                          {userRequest.first_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {userRequest.last_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {userRequest.email_address || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {userRequest.username}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {userRequest.status === 'approved' ? (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={userRequest.team_level || ''}
                              onChange={(e) => handleTeamLevelUpdate(userRequest.id, e.target.value)}
                              disabled={teamLevelUpdateLoading === userRequest.id}
                              displayEmpty
                              sx={{ fontSize: '0.875rem' }}
                            >
                              <MenuItem value="">
                                <em>Select team level</em>
                              </MenuItem>
                              <MenuItem value="High School">High School</MenuItem>
                              <MenuItem value="College D3">College D3</MenuItem>
                              <MenuItem value="College D2">College D2</MenuItem>
                              <MenuItem value="College FCS">College FCS</MenuItem>
                              <MenuItem value="College FBS">College FBS</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body1">
                            {userRequest.team_level || 'N/A'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getRoleChip(userRequest.role)}
                          {userRequest.status === 'approved' && (
                            <FormControl size="small" sx={{ minWidth: 80 }}>
                              <Select
                                value={userRequest.role}
                                onChange={(e) => handleRoleUpdate(userRequest.id, e.target.value)}
                                disabled={roleUpdateLoading === userRequest.id}
                                sx={{ fontSize: '0.75rem' }}
                              >
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatDate(userRequest.created_at)}
                      </TableCell>
                      <TableCell>
                        {getStatusChip(userRequest.status)}
                      </TableCell>
                      <TableCell align="center">
                        {userRequest.status === 'pending' && (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckCircle size={16} />}
                              onClick={() => openConfirmDialog('approve', userRequest)}
                              disabled={actionLoading === userRequest.id}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<XCircle size={16} />}
                              onClick={() => openConfirmDialog('reject', userRequest)}
                              disabled={actionLoading === userRequest.id}
                            >
                              Reject
                            </Button>
                          </Box>
                        )}
                        {userRequest.status !== 'pending' && (
                          <Typography variant="body2" color="text.secondary">
                            No actions available
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {userRequest.status === 'approved' ? (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<Lock size={16} />}
                            onClick={() => setPasswordResetDialog({ open: true, user: userRequest })}
                          >
                            Reset Password
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            onClick={fetchUserRequests}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, user: null })}
      >
        <DialogTitle>
          Confirm {confirmDialog.action === 'approve' ? 'Approval' : 'Rejection'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmDialog.action} the user "{confirmDialog.user?.first_name} {confirmDialog.user?.last_name}" ({confirmDialog.user?.username})?
            {confirmDialog.action === 'approve' && ' This will grant them access to the application.'}
            {confirmDialog.action === 'reject' && ' This will deny them access to the application.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: null, user: null })}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleUserAction(confirmDialog.user?.id, confirmDialog.action)}
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : `${confirmDialog.action === 'approve' ? 'Approve' : 'Reject'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <AdminPasswordReset
        user={user}
        targetUser={passwordResetDialog.user}
        open={passwordResetDialog.open}
        onClose={() => setPasswordResetDialog({ open: false, user: null })}
        onSuccess={() => {
          // Optional: Refresh user list or show success message
          console.log('Password reset successful');
        }}
      />
    </Box>
  );
};

export default AdminDashboard;